import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60000; // refresh count every 60s

const NotificationBell = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/me/count`);
      setCount(res.data.count || 0);
    } catch (e) { /* ignore */ }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/me?limit=15`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchItems();
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleClickItem = async (n) => {
    try {
      await axios.put(`${API}/notifications/me/${n.id}/read`);
    } catch (e) { /* ignore */ }
    setOpen(false);
    fetchCount();
    if (n.link) navigate(n.link);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/me/read-all`);
      setCount(0);
      setItems(items.map((i) => ({ ...i, read: true })));
    } catch (e) { /* ignore */ }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const mins = Math.floor((Date.now() - d.getTime()) / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
      return `${Math.floor(mins / 1440)}d ago`;
    } catch { return ''; }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative h-10 w-10 flex items-center justify-center rounded-lg hover:bg-slate-100 transition"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-slate-700" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-unread-count"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white border-2 border-slate-900 shadow-2xl z-50"
          data-testid="notification-dropdown"
        >
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-widest font-semibold">Notifications</h3>
            {count > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs uppercase tracking-wider text-slate-300 hover:text-white"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">No notifications yet.</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClickItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-200 hover:bg-slate-50 transition ${
                    !n.read ? 'bg-blue-50/50' : ''
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-blue-600 flex-shrink-0" />}
                    <div className={`flex-1 min-w-0 ${n.read ? 'pl-5' : ''}`}>
                      <p className="text-sm font-semibold text-slate-900 truncate">{n.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                        {fmtTime(n.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
